// src/pages/customer/Payments/CustomerPayments.jsx

import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Modal, Pagination } from 'react-bootstrap';
import { 
  FaSearch,
  FaFilter,
  FaFileInvoice,
  FaMoneyBillWave,
  FaCreditCard,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaDownload,
  FaReceipt
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';
import '../../../../assets/css/customer/dashboard/Payments.css';
import CustomerLayout from '../../../../layouts/customer/CustomerLayout';
import AppContext from '../../../../context/AppContext'; // default export, same as BookingDetails.js
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

// Same PaymentForm pattern as BookingDetails.js — createPaymentIntent → confirmCardPayment → confirmPayment
const PaymentForm = ({ bookingId, amount, baseUrl, onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [payLoading, setPayLoading] = useState(false);
  const [cardError, setCardError] = useState('');

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setPayLoading(true);
    setCardError('');

    try {
      const intentRes = await axios.post(`${baseUrl}/customer/payments/create-intent`, {
        booking_id: bookingId,
      });

      if (!intentRes.data.success) {
        setCardError('Could not create the payment intent. Please try again.');
        setPayLoading(false);
        return;
      }

      const clientSecret = intentRes.data.clientSecret;
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) {
        setCardError(result.error.message);
        setPayLoading(false);
        return;
      }

      if (result.paymentIntent.status === 'succeeded') {
        await axios.post(`${baseUrl}/customer/payments/confirm`, { booking_id: bookingId });
        onPaymentSuccess();
      }
    } catch (err) {
      console.log(err);
      setCardError('Something went wrong. Please try again.');
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div className="cp-payment-form">
      {/* NOTE: backend's createPaymentIntent always charges booking.advanceAmount from the DB,
          regardless of what's shown here. If this row's due amount is actually the
          remainingAmount (advance already paid at booking time), the amount charged
          won't match what's displayed below — flagged for supervisor to confirm. */}
      <p style={{ marginBottom: '10px', fontWeight: '600' }}>
        Amount Due: PKR {Number(amount).toLocaleString('en-PK')}
      </p>
      <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
        <CardElement options={{ style: { base: { fontSize: '16px' } }, hidePostalCode: true }} />
      </div>
      {cardError && <p style={{ color: 'red', fontSize: '13px' }}>{cardError}</p>}
      <button className="bd-confirm-btn" disabled={!stripe || payLoading} onClick={handlePay}>
        {payLoading ? 'Processing...' : `Pay PKR ${Number(amount).toLocaleString('en-PK')}`}
      </button>
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
        Test card: 4242 4242 4242 4242, any future date, any CVC
      </p>
    </div>
  );
};

const Payments = () => {
  const { baseUrl } = useContext(AppContext);

  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingType, setBookingType] = useState('venue'); // 'venue' or 'package'

  useEffect(() => { fetchPayments(); }, []);

  // Maps a raw Webooking row (with wevenue/weevent/wepackage relations) into
  // the shape this table already renders.
  const mapBookingToPaymentRow = (b) => {
    // TODO: confirm actual Wevenue/Wepackage column names — assumed venueName/city/packageName
    const venueName = b.wevenue?.venueName || b.wepackage?.wevenue?.venueName || b.wepackage?.packageName || 'N/A';
    const venueCity = b.wevenue?.city || b.wepackage?.wevenue?.city || '';

    let status = 'pending';
    if (b.paymentStatus === 'paid') {
      status = 'paid';
    } else if (b.date && new Date(b.date) < new Date()) {
      status = 'overdue';
    }

    return {
      id: b.id,
      bookingId: `BK-${b.id}`,
      venueName,
      venueCity,
      eventType: b.eventType,
      eventDate: b.date,
      amount: b.totalAmount,
      status,
      paymentDate: b.paymentStatus === 'paid' ? b.updated_at : null,
      dueDate: b.date,
      advanceAmount: b.advanceAmount,
      remainingAmount: b.remainingAmount,
      type: b.wevenue_id ? 'venue' : 'package'
    };
  };

  const filterList = (list, search, status, date, type) => {
    let filtered = list.filter(p => p.type === type);
    if (search) filtered = filtered.filter(p => p.bookingId.toLowerCase().includes(search) || p.venueName.toLowerCase().includes(search) || p.eventType.toLowerCase().includes(search));
    if (status) filtered = filtered.filter(p => p.status === status);
    if (date)   filtered = filtered.filter(p => p.eventDate === date);
    return filtered;
  };

  const fetchPayments = () => {
    setLoading(true);
    const customer = JSON.parse(localStorage.getItem('loggedInCustomer'));

    const formData = new FormData();
    formData.append('wecustomer_id', customer?.id);

    axios.post(`${baseUrl}/customer/bookings/viewMyBookings`, formData)
      .then((res) => {
        const mapped = (res.data.bookings || []).map(mapBookingToPaymentRow);
        setPayments(mapped);
        setFilteredPayments(filterList(mapped, searchTerm, filterStatus, filterDate, bookingType));
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching payments:', error);
        setLoading(false);
      });
  };

  const paymentsForType = payments.filter(p => p.type === bookingType);
  const totalBookings = paymentsForType.length;
  const totalPaid    = paymentsForType.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const totalPending = paymentsForType.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
  const totalOverdue = paymentsForType.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0);

  const handleTypeChange = (type) => {
    setBookingType(type);
    setFilteredPayments(filterList(payments, searchTerm, filterStatus, filterDate, type));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredPayments(filterList(payments, value, filterStatus, filterDate, bookingType));
    setCurrentPage(1);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    setFilteredPayments(filterList(payments, searchTerm, value, filterDate, bookingType));
    setCurrentPage(1);
  };

  const handleDateFilter = (e) => {
    const value = e.target.value;
    setFilterDate(value);
    setFilteredPayments(filterList(payments, searchTerm, filterStatus, value, bookingType));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm(''); setFilterStatus(''); setFilterDate('');
    setFilteredPayments(filterList(payments, '', '', '', bookingType));
    setCurrentPage(1);
  };

  const handlePayNow = (booking) => { setSelectedBooking(booking); setShowPaymentModal(true); };

  const handleViewInvoice = (booking) => {
    Swal.fire({ icon: 'info', title: 'Downloading Invoice', text: `Invoice for ${booking.bookingId} is being prepared...`, timer: 2000, showConfirmButton: false });
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    Swal.fire({
      icon: 'success',
      title: 'Payment Successful!',
      text: 'Your payment has been received.',
      confirmButtonColor: '#28a745',
      timer: 2500,
      showConfirmButton: false
    });
    fetchPayments();
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'paid':    return <Badge bg="success" className="status-badge-cp"><FaCheckCircle /> Paid</Badge>;
      case 'pending': return <Badge bg="warning" className="status-badge-cp"><FaClock /> Pending</Badge>;
      case 'overdue': return <Badge bg="danger"  className="status-badge-cp"><FaExclamationTriangle /> Overdue</Badge>;
      default:        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const indexOfLastPayment  = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments     = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages          = Math.ceil(filteredPayments.length / paymentsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  /* ─────────────────────────────────────────
     BUTTON STYLES
     - View Invoice : mint green (#7fd3c1) pill
     - Pay Now      : ALL same blue-indigo gradient pill
  ───────────────────────────────────────── */

  /* Shared base for both buttons — same pill shape */
  const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '8px 18px',
    fontSize: '13px',
    fontWeight: '700',
    borderRadius: '50px',          /* pill shape */
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.22s ease',
    whiteSpace: 'nowrap',
    letterSpacing: '0.3px',
  };

  /* ✅ View Invoice — mint green */
  const viewInvoiceStyle = {
    ...btnBase,
    background: 'linear-gradient(135deg, #7fd3c1, #5bbfaa)',
    color: '#ffffff',
    boxShadow: '0 4px 14px rgba(127,211,193,0.45)',
  };

  /* ✅ Pay Now — all same blue-indigo (pending AND overdue) */
  const payNowStyle = {
    ...btnBase,
    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    color: '#ffffff',
    boxShadow: '0 4px 14px rgba(99,102,241,0.38)',
  };

  const onHoverUp   = (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.88'; };
  const onHoverDown = (e) => { e.currentTarget.style.transform = 'translateY(0)';    e.currentTarget.style.opacity = '1'; };

  return (
    <CustomerLayout>
    <div className="customer-payments-page-cp">
      <Container fluid>

        {/* Page Header */}
        <div className="page-header-cp">
          <h2 className="page-title-cp"><FaMoneyBillWave className="me-2" />My Payments</h2>
          <p className="page-subtitle-cp">Manage your booking payments and view invoices</p>
        </div>

        {/* Venue / Package toggle */}
        <div className="d-flex gap-2 mb-4">
          <Button
            variant={bookingType === 'venue' ? 'primary' : 'outline-primary'}
            onClick={() => handleTypeChange('venue')}
          >
            Venue Payments
          </Button>
          <Button
            variant={bookingType === 'package' ? 'primary' : 'outline-primary'}
            onClick={() => handleTypeChange('package')}
          >
            Package Payments
          </Button>
        </div>

        {/* Statistics Cards */}
        <Row className="stats-section-cp mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="stat-card-cp card-bookings-cp">
              <Card.Body>
                <div className="stat-icon-cp"><FaReceipt /></div>
                <div className="stat-content-cp"><h3>{totalBookings}</h3><p>Total Bookings</p></div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stat-card-cp card-paid-cp">
              <Card.Body>
                <div className="stat-icon-cp"><FaCheckCircle /></div>
                <div className="stat-content-cp"><h3>{(totalPaid / 1000000).toFixed(2)}M</h3><p>Total Paid (PKR)</p></div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stat-card-cp card-pending-cp">
              <Card.Body>
                <div className="stat-icon-cp"><FaClock /></div>
                <div className="stat-content-cp"><h3>{(totalPending / 1000).toFixed(0)}K</h3><p>Pending (PKR)</p></div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stat-card-cp card-overdue-cp">
              <Card.Body>
                <div className="stat-icon-cp"><FaExclamationTriangle /></div>
                <div className="stat-content-cp"><h3>{(totalOverdue / 1000).toFixed(0)}K</h3><p>Overdue (PKR)</p></div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="filters-card-cp mb-4">
          <Card.Body>
            <Row className="g-3">
              <Col lg={4} md={6}>
                <div className="search-box-cp">
                  <FaSearch className="search-icon-cp" />
                  <Form.Control type="text" placeholder="Search by Booking ID or Venue Name..." value={searchTerm} onChange={handleSearch} className="search-input-cp" />
                </div>
              </Col>
              <Col lg={3} md={6}>
                <Form.Select value={filterStatus} onChange={handleStatusFilter} className="filter-select-cp">
                  <option value="">All Status</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="overdue">Overdue</option>
                </Form.Select>
              </Col>
              <Col lg={3} md={6}>
                <Form.Control type="date" value={filterDate} onChange={handleDateFilter} className="filter-date-cp" />
              </Col>
              <Col lg={2} md={6}>
                <Button variant="outline-secondary" className="reset-btn-cp w-100" onClick={handleResetFilters}>
                  <FaFilter /> Reset
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Payments Table */}
        <Card className="table-card-cp">
          <Card.Body>
            <div className="table-responsive">
              <Table hover className="payments-table-cp">
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Venue Name</th>
                    <th>Event</th>
                    <th>Date</th>
                    <th>Amount (PKR)</th>
                    <th>Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPayments.length > 0 ? (
                    currentPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="fw-bold text-primary">{payment.bookingId}</td>
                        <td>
                          <div>{payment.venueName}</div>
                          <small className="text-muted">{payment.venueCity}</small>
                        </td>
                        <td>{payment.eventType}</td>
                        <td>{new Date(payment.eventDate).toLocaleDateString('en-GB')}</td>
                        <td className="fw-bold">{payment.amount.toLocaleString()}</td>
                        <td>{getStatusBadge(payment.status)}</td>
                        <td className="text-center">
                          {payment.status === 'paid' ? (
                            /* ✅ View Invoice — mint green pill */
                            <button
                              style={viewInvoiceStyle}
                              onClick={() => handleViewInvoice(payment)}
                              onMouseEnter={onHoverUp}
                              onMouseLeave={onHoverDown}
                            >
                              <FaFileInvoice style={{ fontSize: '13px' }} />
                              View Invoice
                            </button>
                          ) : (
                            /* ✅ Pay Now — same blue-indigo for ALL (pending + overdue) */
                            <button
                              style={payNowStyle}
                              onClick={() => handlePayNow(payment)}
                              onMouseEnter={onHoverUp}
                              onMouseLeave={onHoverDown}
                            >
                              <FaCreditCard style={{ fontSize: '13px' }} />
                              Pay Now
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <p className="text-muted mb-0">No payment records found</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
                  {[...Array(totalPages)].map((_, index) => (
                    <Pagination.Item key={index + 1} active={index + 1 === currentPage} onClick={() => paginate(index + 1)}>
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>

      </Container>

      {/* Payment Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg" centered>
        <Modal.Header closeButton className="payment-modal-header-cp">
          <Modal.Title><FaCreditCard className="me-2" />Make Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="payment-modal-body-cp">
          {selectedBooking && (
            <>
              <div className="booking-details-cp mb-4">
                <Row>
                  <Col md={6}>
                    <p><strong>Booking ID:</strong> {selectedBooking.bookingId}</p>
                    <p><strong>Venue:</strong> {selectedBooking.venueName} - {selectedBooking.venueCity}</p>
                    <p><strong>Event:</strong> {selectedBooking.eventType}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Date:</strong> {new Date(selectedBooking.eventDate).toLocaleDateString('en-GB')}</p>
                  </Col>
                </Row>
              </div>

              <Elements stripe={stripePromise}>
                <PaymentForm
                  bookingId={selectedBooking.id}
                  amount={selectedBooking.amount}
                  baseUrl={baseUrl}
                  onPaymentSuccess={handlePaymentSuccess}
                />
              </Elements>
            </>
          )}
        </Modal.Body>
      </Modal>

    </div>
    </CustomerLayout>
  );
};

export default Payments;